import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { AuthService } from "@app/core/services/auth.service";
import { DomainService } from "@app/core/services/domain.service";
import { IllnessService } from "@app/core/services/illness.service";
import { MonitoringService } from "@app/core/services/monitoring.service";
import { PatientsService } from "@app/core/services/patients.service";
import { SwUpdatesService } from "@app/core/services/sw-updates.service";
import { ToastService } from "@app/core/services/toast.service";
import { UsersService } from "@app/core/services/users.service";
import { FirstVisitDialogComponent } from "@app/shared/components/dialogs/first-visit-dialog/first-visit-dialog.component";
import { DomainsKey } from "@app/shared/enums/domains";
import { Patient } from "@app/shared/models";
import { compareArrays } from "@app/shared/utils/compare-arrays";
import * as fromAuth from "@app/store";
import { TranslocoService } from "@jsverse/transloco";
import { DialogService } from "@ngneat/dialog";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { concatLatestFrom } from "@ngrx/operators";
import { Store, select } from "@ngrx/store";
import { of } from "rxjs";
import {
  catchError,
  delay,
  exhaustMap,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from "rxjs/operators";
import * as AuthActions from "../actions/auth.actions";
import * as AuthSelectors from "../selectors/auth.selectors";

import * as DietarySupplementActions from "@app/dietary-supplement/store/actions/dietary-supplement.actions";

// import { translate } from '@jsverse/transloco';

// Функция для сброса кеша у продуктов (нужно чтобы обновить показатели с предупреждением о наличии дефектов у пациента)
const warningChangeConditions = (
  patient: Patient | null,
  oldPatient: Patient | null,
) => {
  const newDiscomfortProducts =
    patient?.attributes?.data?.discomfortProduct?.products;
  const oldDiscomfortProducts =
    oldPatient?.attributes?.data?.discomfortProduct?.products;

  const newHatredProducts = patient?.attributes?.data?.hatredProducts;
  const oldHatredProducts = oldPatient?.attributes?.data?.hatredProducts;

  const newDietRestrictions = patient?.attributes?.data?.diet_restrictions;
  const oldDietRestrictions = oldPatient?.attributes?.data?.diet_restrictions;

  const newChronicIllnesses = patient?.attributes?.data?.chronicDiseases;
  const oldChronicIllnesses = oldPatient?.attributes?.data?.chronicDiseases;

  const newAllergies = patient?.attributes?.data?.allergyProduct?.products;
  const oldAllergies = oldPatient?.attributes?.data?.allergyProduct?.products;

  return (
    !compareArrays(newDiscomfortProducts, oldDiscomfortProducts) ||
    !compareArrays(newHatredProducts, oldHatredProducts) ||
    !compareArrays(newDietRestrictions, oldDietRestrictions) ||
    !compareArrays(newChronicIllnesses, oldChronicIllnesses) ||
    !compareArrays(newAllergies, oldAllergies)
  );
};

const resetCacheIntoProducts = (store: Store) => {
  store.dispatch(DietarySupplementActions.resetCache());
};

@Injectable()
export class AuthEffects {
  checkAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AuthActions.checkAuth,
        AuthActions.loginSuccess,
        AuthActions.verifyEmailSuccess,
      ),
      switchMap(() => {
        return this.authService.isAuthenticated().pipe(
          map((isAuthenticated: boolean) =>
            isAuthenticated
              ? AuthActions.checkAuthSuccess({ isAuthenticated })
              : AuthActions.checkAuthFailure({
                  isAuthenticated: false,
                }),
          ),
        );
      }),
    ),
  );

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      switchMap(({ userCredentials }) => {
        return this.authService.login(userCredentials).pipe(
          map((response) => {
            if (response.message) {
              this.toastService.success(response.message);
              this.monitoringService.sendEventToAplitude("event_auth");
            }

            return AuthActions.loginSuccess();
          }),
          catchError((error) => {
            return of(AuthActions.loginFailure({ error }));
          }),
        );
      }),
    ),
  );

  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginSuccess),
        withLatestFrom(
          this.store.pipe(select(AuthSelectors.selectRedirectUrl)),
        ),
        tap(([_, url]) => {
          if (url) {
            this.router.navigateByUrl(url);
          } else {
            this.router.navigate(["/"]);
          }
        }),
      ),
    { dispatch: false },
  );

  loginRedirect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          AuthActions.loginRedirect,
          AuthActions.registrationSuccess,
          AuthActions.logoutSuccess,
          AuthActions.verifyEmailFailure,
        ),
        tap(() => this.router.navigate(["/auth"])),
      ),
    { dispatch: false },
  );

  registration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.registration),
      switchMap(({ userCredentials }) => {
        return this.authService.registration(userCredentials).pipe(
          map((response) => {
            this.toastService.success(response.message);
            this.monitoringService.sendEventToAplitude("event_registred");

            return AuthActions.registrationSuccess();
          }),
          catchError((error) => {
            return of(AuthActions.registrationFailure({ error }));
          }),
        );
      }),
    ),
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      switchMap(() => {
        return this.authService.logout().pipe(
          map(() => {
            return AuthActions.logoutSuccess();
          }),
          catchError((error) => {
            return of(AuthActions.logoutFailure({ error }));
          }),
        );
      }),
    ),
  );

  logoutSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logoutSuccess),
        tap(() => this.authService.clearSession()),
      ),
    { dispatch: false },
  );

  checkEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.checkEmail),
      switchMap(({ email }) => {
        return this.authService.checkEmail(email).pipe(
          map((response) => {
            this.toastService.success(response.message);

            return AuthActions.checkEmailSuccess();
          }),
          catchError((error) => {
            return of(AuthActions.checkEmailFailure({ error }));
          }),
        );
      }),
    ),
  );

  verifyEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.verifyEmail),
      concatLatestFrom(() => [
        this.store.pipe(select(AuthSelectors.selectActiveVerifyEmailId)),
        this.store.pipe(select(AuthSelectors.selectActiveVerifyEmailHash)),
        this.store.pipe(select(AuthSelectors.selectActiveVerifyEmailExpires)),
        this.store.pipe(select(AuthSelectors.selectActiveVerifyEmailSignature)),
      ]),
      switchMap(([_, id, hash, expires, signature]) => {
        const verifyEmail = { id, hash, expires, signature };

        return this.authService.verifyEmail(verifyEmail).pipe(
          map((response) => {
            if (response.message) {
              this.toastService.success(response.message);
            }

            return AuthActions.verifyEmailSuccess();
          }),
          tap(() => this.router.navigate(["/"])),
          catchError((error) => {
            return of(AuthActions.verifyEmailFailure({ error }));
          }),
        );
      }),
    ),
  );

  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.resetPassword),
      concatLatestFrom(() => [
        this.store.pipe(select(AuthSelectors.selectActiveResetPasswordToken)),
        this.store.pipe(select(AuthSelectors.selectActiveResetPasswordEmail)),
      ]),
      switchMap(([{ resetPassword }, token, email]) => {
        resetPassword = {
          ...resetPassword,
          token,
          email,
        };

        return this.authService.resetPassword(resetPassword).pipe(
          map(() => {
            this.toastService.success(
              this.translocoService.translate("auth.user-redirect"),
            );

            return AuthActions.resetPasswordSuccess();
          }),
          delay(5000),
          tap(() => this.router.navigate(["/"])),
          catchError((error) => {
            return of(AuthActions.resetPasswordFailure({ error }));
          }),
        );
      }),
    ),
  );

  // todo стоит вынести в отдельный файл ..current-user..
  // *Возможно стоит вынести в отдельный файл
  loadCurrentUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.loadCurrentUser, AuthActions.checkAuthSuccess),
      switchMap((action) => {
        let redirectUrl = "";
        if (action.type === AuthActions.loadCurrentUser.type) {
          redirectUrl = action.redirectUrl ?? "";
        }

        return this.usersService.loadUser().pipe(
          switchMap((response) => {
            return [
              AuthActions.monitorCurrentUser({
                user: response.data.attributes,
              }),
              AuthActions.loadCurrentUserSuccess({
                user: response.data.attributes,
                redirectUrl,
              }),
              AuthActions.loadCurrentPatientSuccess({
                patient: response.data.meta.patient,
              }),
              ...(response.data.attributes.first_visit
                ? [AuthActions.openUserFirstVisitDialog()]
                : []),
            ];
          }),
          catchError((error) => {
            return of(AuthActions.loadCurrentUserFailure({ error }));
          }),
        );
      }),
    );
  });

  monitorCurrentUser$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.monitorCurrentUser),
        tap(({ user }) => this.monitoringService.monitorCurrentUser(user)),
      ),
    { dispatch: false },
  );

  // *Возможно стоит вынести в отдельный файл
  updateCurrentUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.updateCurrentUser),
      switchMap(({ user }) => {
        return this.usersService.updateUser(user).pipe(
          switchMap((response) => {
            this.toastService.success(response.data.msg);

            return [
              AuthActions.updateCurrentUserSuccess({
                user: response.data.attributes,
              }),
              AuthActions.updateCurrentPatientSuccess({
                patient: response.data.meta.patient,
              }),
            ];
          }),
          catchError((error) => {
            return of(AuthActions.updateCurrentUserFailure({ error }));
          }),
        );
      }),
    ),
  );

  updateCurrentPatient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.updateCurrentPatient),
      concatLatestFrom(() => [
        this.store.pipe(select(AuthSelectors.selectCurrentPatient())),
      ]),
      switchMap(([{ patient }, currentPatient]) => {
        return this.patientsService.updatePatient(patient).pipe(
          map((response) => {
            const newPatient = response.data.patient;
            this.toastService.success(
              this.translocoService.translate("user-update"),
            );
            this.illnessService.pushUpdatePatientEvent(
              "success",
              response.data,
            );

            if (warningChangeConditions(newPatient, currentPatient)) {
              resetCacheIntoProducts(this.store);
            }

            return AuthActions.updateCurrentPatientSuccess({
              patient: response.data.patient,
            });
          }),
          catchError((error) => {
            this.illnessService.pushUpdatePatientEvent("failure", null);
            this.toastService.error(
              this.translocoService.translate("auth.user-not-update"),
              {},
              7000,
              500,
            );

            return of(AuthActions.updateCurrentPatientFailure({ error }));
          }),
        );
      }),
    ),
  );

  // TODO возможно понадобится позже
  // removeCurrentPatient$ = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(AuthActions.removeCurrentPatient),
  //     switchMap(({ patient }) => {
  //       return this.patientsService.updatePatient(patient).pipe(
  //         map((response) => {
  //           this.toastService.success('Данные клиента обновлены');
  //           return AuthActions.updateCurrentPatientSuccess({
  //             patient: response.data.patient,
  //           });
  //         }),
  //         catchError((error) => {
  //           this.toastService.error('Данные клиента не удалось обновить');
  //           return of(AuthActions.updateCurrentPatientFailure({ error }));
  //         }),
  //       );
  //     }),
  //   ),
  //   { dispatch: false },
  // );

  openUserFirstVisitDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.openUserFirstVisitDialog),
      concatLatestFrom(() => [
        this.store.pipe(select(AuthSelectors.selectCurrentUser())),
      ]),
      exhaustMap(([_, currentUser]) => {
        if (this._domain.getDomain() === DomainsKey.niap) {
          return of(null); // Вернуть пустой Observable, чтобы прервать цепочку
        } else {
          return this.dialogService.open(FirstVisitDialogComponent, {
            data: {
              currentUser,
            },
          }).afterClosed$;
        }
      }),
      map((result) => {
        return result
          ? AuthActions.checkUserFirstVisit({ isUserFirstVisit: result })
          : AuthActions.checkUserFirstVisitDismiss();
      }),
    ),
  );

  checkUserFirstVisit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.checkUserFirstVisit),
      switchMap(({ isUserFirstVisit }) => {
        return this.usersService.changeUserVisit(isUserFirstVisit).pipe(
          map((response) => {
            this.toastService.success(response.message);

            return AuthActions.checkUserFirstVisitSuccess({
              user: response.data.attributes,
            });
          }),
          catchError((error) => {
            return of(AuthActions.checkUserFirstVisitFailure({ error }));
          }),
        );
      }),
    ),
  );

  checkForUpdatesApp$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.checkForUpdatesApp),
        tap(() => this.swUpdatesService.checkForUpdates()),
      ),
    { dispatch: false },
  );

  checkAuthByTicketAndRedirect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.checkAuthByTicketAndRedirect),
      switchMap(({ ticket, redirectUrl }) => {
        return this.authService.checkAuthByTicket(ticket).pipe(
          switchMap(() => {
            return [
              AuthActions.checkAuthByTicketAndRedirectSuccess(),
              AuthActions.loadCurrentUser({ redirectUrl }),
            ];
          }),
          catchError((error) => {
            this.router.navigate(["/auth/login"]);

            return of(
              AuthActions.checkAuthByTicketAndRedirectFailure({ error }),
            );
          }),
        );
      }),
    ),
  );

  loadCurrentUserSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loadCurrentUserSuccess),
        tap((action) => {
          const url = action.redirectUrl;
          if (url) {
            const newUrl = new URL(url);
            this.router.navigateByUrl(newUrl.pathname);
          }
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private store: Store<fromAuth.State>,
    private authService: AuthService,
    private usersService: UsersService,
    private patientsService: PatientsService,
    private illnessService: IllnessService,
    private toastService: ToastService,
    private router: Router,
    private dialogService: DialogService,
    private swUpdatesService: SwUpdatesService,
    private monitoringService: MonitoringService,
    private translocoService: TranslocoService,
    private _domain: DomainService,
  ) {}
}
